import {
  resolvePlanningUnitContactContexts,
  type PlanningUnitContactContext,
  type PlanningUnitContactSummary,
  type PlanningUnitContactTarget,
} from "../../../../../api/kehrblatt/planning"
import { formatPropertyCustomerRole } from "../customer-roles"
import { tooltipList } from "../../../../../_shared/_logic/rich-tooltips"

const CONTACT_CONTEXT_BATCH_SIZE = 600

export const planningUnitContactContextKey = (
  propertyId: string | null | undefined,
  propertyUnitId?: string | null,
): string => `${String(propertyId ?? "").trim()}::${String(propertyUnitId ?? "").trim()}`

export const planningUnitContactContextMap = (
  contexts: PlanningUnitContactContext[] | null | undefined,
): Map<string, PlanningUnitContactContext> =>
  new Map(
    (contexts ?? []).map((context) => [
      planningUnitContactContextKey(context.property_id, context.property_unit_id),
      context,
    ]),
  )

export const planningUnitContactTargetsForProperties = (
  properties: Property[] | null | undefined,
): PlanningUnitContactTarget[] => {
  const targets: PlanningUnitContactTarget[] = []
  const seen = new Set<string>()
  for (const property of properties ?? []) {
    const propertyTargets: PlanningUnitContactTarget[] = [
      { property_id: property.id, property_unit_id: null },
      ...(property.units ?? []).map((unit) => ({
        property_id: property.id,
        property_unit_id: String(unit.id),
      })),
    ]
    for (const target of propertyTargets) {
      const key = planningUnitContactContextKey(target.property_id, target.property_unit_id)

      if (!target.property_id || seen.has(key)) continue

      seen.add(key)
      targets.push(target)
    }
  }

  return targets
}

export const planningUnitContactTargetsForWorkOrders = (
  workOrders: WorkOrder[] | null | undefined,
): PlanningUnitContactTarget[] => {
  const targets: PlanningUnitContactTarget[] = []
  const seen = new Set<string>()
  for (const workOrder of workOrders ?? []) {
    const propertyId = String(workOrder.property_id ?? "").trim()

    if (!propertyId) continue

    const target = {
      property_id: propertyId,
      property_unit_id: workOrder.property_unit_id ? String(workOrder.property_unit_id) : null,
    }
    const key = planningUnitContactContextKey(target.property_id, target.property_unit_id)

    if (seen.has(key)) continue

    seen.add(key)
    targets.push(target)
  }

  return targets
}

export const loadMissingPlanningUnitContactContexts = async ({
  organizationId,
  targets,
  existing = [],
}: {
  organizationId: string
  targets: PlanningUnitContactTarget[]
  existing?: PlanningUnitContactContext[]
}): Promise<PlanningUnitContactContext[]> => {
  const existingByKey = planningUnitContactContextMap(existing)
  const missing = targets.filter(
    (target) =>
      !existingByKey.has(
        planningUnitContactContextKey(target.property_id, target.property_unit_id),
      ),
  )

  if (!organizationId || !missing.length) return [...existingByKey.values()]

  for (let offset = 0; offset < missing.length; offset += CONTACT_CONTEXT_BATCH_SIZE) {
    const response = await resolvePlanningUnitContactContexts({
      organization_id: organizationId,
      targets: missing.slice(offset, offset + CONTACT_CONTEXT_BATCH_SIZE),
    })
    for (const context of response.items ?? []) {
      existingByKey.set(
        planningUnitContactContextKey(context.property_id, context.property_unit_id),
        context,
      )
    }
  }

  return [...existingByKey.values()]
}

export const planningContactLabel = (
  contact: PlanningUnitContactSummary | null | undefined,
): string | null => {
  const name = String(contact?.display_name ?? "").trim()

  if (!name) return null

  return `${formatPropertyCustomerRole(contact?.role)} ${name}`
}

export const appendPlanningContactSubLabel = (
  baseLabel: string | null | undefined,
  context: PlanningUnitContactContext | null | undefined,
): string | null => {
  const base = String(baseLabel ?? "").trim()
  const contact = planningContactLabel(context?.primary_contact)

  return [base, contact].filter(Boolean).join(" · ") || null
}

export const planningContactsTooltipList = (
  context: PlanningUnitContactContext | null | undefined,
): string | null =>
  tooltipList(
    "Ansprechpartner",
    (context?.contacts ?? []).map(
      (contact) => `${formatPropertyCustomerRole(contact.role)}: ${contact.display_name}`,
    ),
  )
